import { IS_LOGIN_KEY } from "./enum";
import * as pdfjsLib from "pdfjs-dist";
import "pdfjs-dist/build/pdf.worker.entry";
import dayjs from "dayjs";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  process.env.BASE_URL + "/libs/pdf.worker.min.js";

/**
 * 判断用户是否登录
 * @returns {string}
 */
export function isLogin() {
  return localStorage.getItem(IS_LOGIN_KEY);
}

/**
 * PDF操作控制器
 */
export class PDFController {
  #pdfDocument;
  #numPages;

  constructor() {}

  /**
   * 读取文件并渲染第一页
   * @param file 文件对象
   * @param canvas 画板
   * @returns {Promise<void>}
   */
  async readFile(file, canvas, callback) {
    // 加载pdf文件
    let loadingTask = null;
    if (file instanceof Array) {
      loadingTask = pdfjsLib.getDocument(file);
    } else {
      const fileBuffer = await this.#readFileAsArrayBuffer(file);
      loadingTask = pdfjsLib.getDocument(fileBuffer);
    }

    // 对外暴露加载进度
    loadingTask.onProgress = (progress) => {
      if (callback) {
        callback(progress);
      }
    };

    // 获得pdf对象
    this.#pdfDocument = await loadingTask.promise;
    this.#numPages = this.#pdfDocument.numPages;
    return this.#pdfDocument;
  }

  /**
   * 获取PDF文件的指定页码页面
   * @param num
   * @returns {Promise<*>}
   */
  async getPage(num) {
    if (num > this.#numPages) {
      console.error("页数超过最大页码");
      return;
    }

    return await this.#pdfDocument.getPage(num);
  }

  /**
   * 读取文件的二进制信息
   * @param file
   * @returns {Promise<unknown>}
   */
  #readFileAsArrayBuffer(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * 渲染指定的页面到页面画板
   * @param page 页码
   * @param canvas 画板
   * @returns {Promise<void>}
   */
  async renderPageToCanvas(page, canvas) {
    try {
      // 1. 获取设备像素比
      const dpr = window.devicePixelRatio || 1;

      // 2. 获取基础 viewport（scale 可以根据需要调整）
      const baseScale = 1; // 基础缩放，可以调整为 1.5 或 2 以获得更高清晰度
      const viewport = page.getViewport({ scale: baseScale });

      // 3. 计算实际渲染尺寸
      const cssWidth = viewport.width;
      const cssHeight = viewport.height;
      const renderWidth = Math.floor(cssWidth * dpr);
      const renderHeight = Math.floor(cssHeight * dpr);

      // 4. 设置 Canvas 样式尺寸（显示尺寸）
      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${cssHeight}px`;

      // 5. 设置 Canvas 实际像素尺寸（渲染尺寸）
      canvas.width = renderWidth;
      canvas.height = renderHeight;

      // 6. 创建适配 DPR 的 viewport
      const renderViewport = page.getViewport({
        scale: baseScale * dpr,
      });

      // 7. 获取 Canvas 上下文
      const context = canvas.getContext("2d");

      // 8. 设置高清渲染（可选，但建议添加）
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";

      // 9. 渲染 PDF 页面
      await page.render({
        canvasContext: context,
        viewport: renderViewport,
      }).promise;

      console.log(
        `渲染完成: ${cssWidth}x${cssHeight} (CSS) -> ${renderWidth}x${renderHeight} (实际像素)`
      );
    } catch (err) {
      console.error("渲染失败:", err);
    }
  }

  /**
   * 销魂对象保证内存
   */
  destroy() {
    if (this.#pdfDocument) {
      this.#pdfDocument.destroy();
      this.#pdfDocument = null;
    }
  }
}

/**
 * 选择PDF文件，直接返回文件对象
 * @returns {Promise<File|File[]|null>} 返回文件对象或文件数组，取消返回null
 */
export function choosePDF() {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,application/pdf";

    input.onchange = (e) => {
      const files = Array.from(e.target.files || []);

      // 统一返回结构：包含file对象和name的数组
      const result = files.map((file) => ({
        file: file,
        name: file.name,
        size: file.size,
        type: file.type,
      }));

      resolve(result);
    };

    input.oncancel = () => resolve([]);
    input.click();
  });
}

/**
 * 交换数组的两个位置
 * @param arr 数组对象
 * @param index1 第一个位置
 * @param index2 第二个位置
 * @returns {*}
 */
export function swapItems(arr, index1, index2) {
  if (!Array.isArray(arr)) {
    throw new Error("第一个参数必须是数组");
  }
  if (
    index1 < 0 ||
    index1 >= arr.length ||
    index2 < 0 ||
    index2 >= arr.length
  ) {
    throw new Error("索引超出数组范围");
  }
  if (index1 === index2) {
    return arr;
  }

  const temp = arr[index1];
  arr[index1] = arr[index2];
  arr[index2] = temp;

  return arr;
}

/**
 * 定时任务
 */
export class Schedule {
  static TASK_TYPE = {
    DAY: "daily",
    ONCE: "once",
  };

  static LOCAL_STORAGE_SCHEDULE_KEY = "schedule";

  constructor() {
    this.id = null;
    this.tasks = new Map();
    this.timer = new Map();
    this.onTaskExecute = new Map();
    this.showQueue = [];
    this.isShowing = false;

    this.initStorage();
  }

  initStorage() {
    const storage = this.getStorage();

    if (storage && Object.keys(storage).length > 0) {
      const lastTime = storage.time;
      if (lastTime !== dayjs().format("YYYY-MM-DD")) {
        this.setStorage({
          time: dayjs().format("YYYY-MM-DD"),
          doneTaskId: [],
        });
        return;
      }
      return;
    }

    this.setStorage({
      time: dayjs().format("YYYY-MM-DD"),
      doneTaskId: [],
    });
  }

  getStorage() {
    return JSON.parse(
      localStorage.getItem(Schedule.LOCAL_STORAGE_SCHEDULE_KEY)
    );
  }

  setStorage(storage) {
    localStorage.setItem(
      Schedule.LOCAL_STORAGE_SCHEDULE_KEY,
      JSON.stringify(storage)
    );
  }

  _enqueueShow(task) {
    this.showQueue.push(task);
    this._tryShowNext();
  }

  _tryShowNext() {
    if (this.isShowing || this.showQueue.length === 0) return;

    this.isShowing = true;
    const nextTask = this.showQueue.shift();

    const showFunc = this.onTaskExecute.get("show");
    if (showFunc) {
      showFunc(nextTask);
    } else {
      this.isShowing = false;
      this._tryShowNext();
    }
  }

  notifyClosed() {
    this.isShowing = false;
    this._tryShowNext();
  }

  addTaskList(taskList) {
    taskList.forEach((task) => {
      if (this.tasks.has(task.taskId)) {
        return;
      }
      const storage = this.getStorage();

      if (storage.doneTaskId.includes(task.taskId)) {
        console.log("任务完成" + task);
        return;
      }
      this.tasks.set(task.taskId, task);
      this.register(task);
    });

    console.log(this.showQueue);
  }

  register(task) {
    const moment = new Date().getTime();
    if (!task.executionTime) return;

    if (task.taskType === Schedule.TASK_TYPE.ONCE) {
      if (task.createTime) {
        const createDate = dayjs(task.createTime).startOf("day");
        const today = dayjs().startOf("day");
        if (today.isAfter(createDate)) return;
      }
      const taskTime = this.formatDay(task.executionTime).getTime();
      const delay = taskTime - moment;

      console.log(task, delay);
      this._enqueueShow(task);
    }

    if (task.taskFrequency === Schedule.TASK_TYPE.DAY) {
      this._enqueueShow(task);
    }
  }

  on(id, func) {
    if (!this.onTaskExecute.has(id)) {
      this.onTaskExecute.set(id, func);
    }
  }

  off() {
    this.timer.forEach((value) => {
      clearTimeout(value);
    });
  }

  doneSchedule(taskId) {
    const storage = this.getStorage();
    storage.doneTaskId.push(taskId);
    this.setStorage(storage);
  }

  formatDay(timeStr) {
    if (!timeStr) {
      return;
    }

    const today = new Date();
    const [hours, minutes, seconds] = timeStr.split(":").map(Number);
    return new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      hours,
      minutes,
      seconds
    );
  }
}

/**
 * 单例WebSocket客户端（支持心跳、断线重连、消息队列、URL参数）
 * 修复：关闭码1006错误、心跳响应兼容服务端 {"code":0,"data":"ping"} 格式
 */
export class WebSocketClient {
  static instance = null;

  /**
   * 获取单例实例
   * @param {Object} config 配置对象（仅首次调用时生效）
   * @returns {WebSocketClient} 单例实例
   */
  static getInstance(config = {}) {
    if (!WebSocketClient.instance) {
      WebSocketClient.instance = new WebSocketClient(config);
    }
    return WebSocketClient.instance;
  }

  /**
   * 构造函数（私有，请使用 getInstance）
   * @param {Object} config 配置参数
   */
  constructor(config) {
    if (WebSocketClient.instance) {
      throw new Error("请使用 WebSocketClient.getInstance() 获取实例");
    }

    // 默认配置
    this.config = {
      baseUrl: config.baseUrl || "", // 基础WebSocket地址（不含参数）
      params: config.params || {}, // 固定参数，如 { platform: 'web' }
      heartbeatInterval: config.heartbeatInterval || 15000, // 心跳发送间隔(ms)
      heartbeatTimeout: config.heartbeatTimeout || 5000, // 心跳响应超时(ms)
      maxReconnectAttempts: config.maxReconnectAttempts || 5, // 最大重连次数
      reconnectBaseDelay: config.reconnectBaseDelay || 1000, // 重连基础延迟(ms)
      maxReconnectDelay: config.maxReconnectDelay || 30000, // 最大重连延迟(ms)
      enableQueue: config.enableQueue !== false, // 是否启用消息队列
      pingMessage: config.pingMessage || JSON.stringify({ type: "ping" }), // 心跳发送消息
      // 默认心跳响应判断：支持 {"type":"pong"} 或 {"code":0,"data":"ping"}
      pongChecker:
        config.pongChecker ||
        ((data) => {
          try {
            const msg = JSON.parse(data);
            return msg.type === "pong" || msg.data === "ping";
          } catch (e) {
            return false;
          }
        }),
    };

    // 状态变量
    this.ws = null;
    this.isManualClose = false;
    this.reconnectCount = 0;
    this.reconnectTimer = null;
    this.heartbeatIntervalTimer = null;
    this.heartbeatTimeoutTimer = null;
    this.messageQueue = [];
    this.callbacks = {
      onOpen: null,
      onMessage: null,
      onClose: null,
      onError: null,
      onReconnect: null,
      onMaxReconnectFailed: null,
    };

    this._onOpenHandler = this._onOpen.bind(this);
    this._onMessageHandler = this._onMessage.bind(this);
    this._onCloseHandler = this._onClose.bind(this);
    this._onErrorHandler = this._onError.bind(this);

    // 如果提供了 baseUrl，自动连接
    if (this.config.baseUrl) {
      this.connect();
    }
  }

  /**
   * 设置用户（动态更新URL参数）
   * @param {string|Object} userName 用户名或参数对象
   * @param {Object} additionalParams 额外参数（当userName为字符串时使用）
   */
  setUser(userName, additionalParams = {}) {
    let newParams = {};
    if (typeof userName === "string") {
      newParams = { userName, ...additionalParams };
    } else if (typeof userName === "object") {
      newParams = { ...userName };
    } else {
      return;
    }
    this.config.params = { ...this.config.params, ...newParams };
    this.reconnect(true); // 强制重连
  }

  /**
   * 获取当前完整URL
   * @returns {string}
   */
  getUrl() {
    if (!this.config.baseUrl) return "";
    const url = new URL(this.config.baseUrl);
    const params = { ...this.config.params };
    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined && params[key] !== null) {
        url.searchParams.set(key, params[key]);
      }
    });
    return url.toString();
  }

  /**
   * 设置事件回调
   * @param {Object} callbacks 回调函数对象
   */
  setCallbacks(callbacks) {
    Object.assign(this.callbacks, callbacks);
  }

  /**
   * 连接WebSocket
   * @param {string} customUrl 可选，自定义完整URL
   */
  connect(customUrl) {
    const url = customUrl || this.getUrl();
    if (!url) {
      console.error("[WebSocketClient] URL未配置");
      return;
    }

    // 关闭现有连接（不触发重连）
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING)
    ) {
      this.close(true);
    }

    // 清除重连定时器
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    try {
      this.ws = new WebSocket(url);
      this.ws.addEventListener("open", this._onOpenHandler);
      this.ws.addEventListener("message", this._onMessageHandler);
      this.ws.addEventListener("close", this._onCloseHandler);
      this.ws.addEventListener("error", this._onErrorHandler);
    } catch (error) {
      console.error("[WebSocketClient] 连接创建失败:", error);
      this._scheduleReconnect();
    }
  }

  /**
   * 强制重连
   * @param {boolean} resetCount 是否重置重连计数
   */
  reconnect(resetCount = false) {
    if (resetCount) {
      this.reconnectCount = 0;
    }
    this.close(false); // 非手动关闭，会触发重连逻辑
  }

  /**
   * 发送消息
   * @param {string|Object|ArrayBuffer} data 要发送的数据
   * @returns {boolean} 是否立即发送成功
   */
  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      let sendData = data;
      if (typeof data === "object" && !(data instanceof ArrayBuffer)) {
        sendData = JSON.stringify(data);
      }
      this.ws.send(sendData);
      return true;
    }
    if (this.config.enableQueue) {
      this.messageQueue.push(data);
      if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
        // 等待打开后自动发送
      } else {
        this.connect();
      }
      return false;
    }
    console.warn("[WebSocketClient] 连接未打开，消息丢弃:", data);
    return false;
  }

  /**
   * 手动关闭连接
   * @param {boolean} manual 是否手动关闭（阻止重连）
   * @param {number} code 关闭状态码（必须为 1000 或 3000-4999）
   * @param {string} reason 关闭原因
   */
  close(manual = true, code = 1000, reason = "") {
    this.isManualClose = manual;
    this._stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      if (
        this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING
      ) {
        // 确保关闭码合法
        const validCode =
          code === 1000 || (code >= 3000 && code <= 4999) ? code : 1000;
        this.ws.close(validCode, reason);
      }
      this._removeEventListeners();
      this.ws = null;
    }
  }

  /**
   * 获取连接状态
   * @returns {number}
   */
  getReadyState() {
    return this.ws ? this.ws.readyState : WebSocket.CLOSED;
  }

  /**
   * 是否已连接
   * @returns {boolean}
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * 销毁实例
   */
  destroy() {
    this.close(true);
    if (WebSocketClient.instance === this) {
      WebSocketClient.instance = null;
    }
  }

  // ==================== 私有方法 ====================

  _removeEventListeners() {
    if (!this.ws) return;
    this.ws.removeEventListener("open", this._onOpenHandler);
    this.ws.removeEventListener("message", this._onMessageHandler);
    this.ws.removeEventListener("close", this._onCloseHandler);
    this.ws.removeEventListener("error", this._onErrorHandler);
  }

  _onOpen(event) {
    console.log("[WebSocketClient] 连接成功");
    this.reconnectCount = 0;
    this.isManualClose = false;
    this._startHeartbeat();
    if (this.config.enableQueue && this.messageQueue.length) {
      const queue = [...this.messageQueue];
      this.messageQueue = [];
      queue.forEach((msg) => this.send(msg));
    }
    if (this.callbacks.onOpen) {
      this.callbacks.onOpen(event, this);
    }
  }

  _onMessage(event) {
    const data = event.data;
    // 心跳响应处理
    if (this.config.pongChecker(data)) {
      this._clearHeartbeatTimeout();
      return; // 心跳消息不对外透传
    }
    if (this.callbacks.onMessage) {
      this.callbacks.onMessage(data, event, this);
    }
  }

  _onClose(event) {
    console.log(
      `[WebSocketClient] 连接关闭, code: ${event.code}, reason: ${event.reason}`
    );
    this._stopHeartbeat();
    if (this.callbacks.onClose) {
      this.callbacks.onClose(event, this);
    }
    // 只要不是手动关闭，就尝试重连
    if (!this.isManualClose) {
      this._scheduleReconnect();
    }
    if (this.ws) {
      this._removeEventListeners();
      this.ws = null;
    }
  }

  _onError(event) {
    console.error("[WebSocketClient] 连接错误:", event);
    if (this.callbacks.onError) {
      this.callbacks.onError(event, this);
    }
    // 错误时关闭连接（使用合法关闭码，触发重连）
    if (this.ws && this.ws.readyState !== WebSocket.CLOSED) {
      this.close(false, 3000, "Connection error");
    }
  }

  _startHeartbeat() {
    this._stopHeartbeat();
    this.heartbeatIntervalTimer = setInterval(() => {
      this._sendPing();
    }, this.config.heartbeatInterval);
  }

  _stopHeartbeat() {
    if (this.heartbeatIntervalTimer) {
      clearInterval(this.heartbeatIntervalTimer);
      this.heartbeatIntervalTimer = null;
    }
    this._clearHeartbeatTimeout();
  }

  _sendPing() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(this.config.pingMessage);
        this._clearHeartbeatTimeout();
        this.heartbeatTimeoutTimer = setTimeout(() => {
          console.warn("[WebSocketClient] 心跳超时，连接可能已失效");
          // 使用合法关闭码 3000，且 isManualClose = false 以便重连
          this.close(false, 3000, "Heartbeat timeout");
        }, this.config.heartbeatTimeout);
      } catch (error) {
        console.error("[WebSocketClient] 发送心跳失败:", error);
        this.close(false, 3000, "Heartbeat send failed");
      }
    }
  }

  _clearHeartbeatTimeout() {
    if (this.heartbeatTimeoutTimer) {
      clearTimeout(this.heartbeatTimeoutTimer);
      this.heartbeatTimeoutTimer = null;
    }
  }

  _scheduleReconnect() {
    if (this.isManualClose) return;
    if (this.reconnectCount >= this.config.maxReconnectAttempts) {
      console.error(
        `[WebSocketClient] 达到最大重连次数(${this.config.maxReconnectAttempts})，停止重连`
      );
      if (this.callbacks.onMaxReconnectFailed) {
        this.callbacks.onMaxReconnectFailed(this);
      }
      return;
    }
    let delay = Math.min(
      this.config.reconnectBaseDelay * Math.pow(2, this.reconnectCount),
      this.config.maxReconnectDelay
    );
    this.reconnectCount++;
    console.log(
      `[WebSocketClient] 第${this.reconnectCount}次重连，延迟${delay}ms`
    );
    if (this.callbacks.onReconnect) {
      this.callbacks.onReconnect(this.reconnectCount, delay, this);
    }
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
}
